import "./index.scss";
import "../TopNavigation/index.scss";

import { Link, useLocation } from "react-router-dom";
import { goBack } from "../../nonUIFuncs";
import { store } from "../../../Store";

/**
 * Individual navigation button component for the dropdown menu.
 * Renders either a Link or external anchor tag based on the external prop.
 * Automatically adjusts font size based on screen dimensions and title length.
 *
 * @param {Object} props - Component props
 * @param {Function} props.animateMenu - Function to close the navigation menu
 * @param {string} props.pathname - The path to navigate to
 * @param {string} props.icon - Icon identifier for CSS class naming
 * @param {string} props.search - URL search parameters
 * @param {string} props.title - Display text for the button
 * @param {boolean} props.external - Whether this is an external link (default: false)
 * @returns {JSX.Element} Navigation button with icon and responsive text
 */
const NavigationButton = ({
  animateMenu,
  pathname,
  icon,
  search,
  title,
  external = false,
}) => {
  const width = store.getState().store.value.width;
  const height = store.getState().store.value.height;
  let vmin = Math.min(height, width);

  let fontSize = vmin * 0.034;
  if (fontSize < 20) {
    fontSize = 20;
  } else if (fontSize > 50) {
    fontSize = 50;
  }

  if (title.length > 20) {
    fontSize = fontSize * 0.8;
  }

  if (external) {
    return (
      <a href={pathname}>
        <div className="navmenu_button hovercursor">
          <div className={`navmenu_button_container_${icon}`} />
          <div className="navmenu_button_text" style={{ fontSize: fontSize }}>
            {title}
          </div>
        </div>
      </a>
    );
  } else {
    return (
      <Link
        onClick={animateMenu}
        className="navmenu_button hovercursor"
        to={{
          pathname: pathname,
          search: search,
        }}
      >
        <div className={`navmenu_button_container_${icon}`} />
        <div className="navmenu_button_text" style={{ fontSize: fontSize }}>
          {title}
        </div>
      </Link>
    );
  }
};

/**
 * Dropdown navigation menu component that slides in from the top.
 * Provides access to all major app sections including home, dictionary, games, and external links.
 * Menu items are conditionally rendered based on app configuration and available features.
 *
 * @param {Object} props - Component props
 * @param {Function} props.animateMenu - Function to toggle menu visibility with animations
 * @param {Object} props.config - Application configuration object
 * @param {boolean} props.config.Credits - Whether to show credits option
 * @param {boolean} props.config.ShowStorybookCorner - Whether to show storybook corner option
 * @param {boolean} props.config.ShowLinkButton - Whether to show external link button
 * @param {string} props.config.Link - External link URL
 * @returns {JSX.Element} Fullscreen dropdown navigation menu
 */
const NavigationMenu = ({ animateMenu, config }) => {
  const Location = useLocation();
  return (
    <div id="navmenu_fade" className="navmenu_fullscreen" onClick={animateMenu}
      onKeyDown={(e) => { if (e.key === "Escape") animateMenu(); }}>
      <nav id="navmenu_slide" className="navmenu_dropdownmenu" role="navigation" aria-label="Main navigation menu">
        <div className="navmenu_safearea">
          <NavigationButton
            animateMenu={animateMenu}
            pathname="/"
            icon="home"
            search=""
            title="Home"
          />
          <NavigationButton
            animateMenu={animateMenu}
            pathname={goBack(Location).pathname}
            icon="back"
            search={goBack(Location).search}
            title="Back"
          />
          <NavigationButton
            animateMenu={animateMenu}
            pathname="/dictionary"
            icon="menu"
            search=""
            title="Dictionary"
          />
          {store.getState().store.value.historyData !== undefined ? (
            <NavigationButton
              animateMenu={animateMenu}
              pathname="/timeline"
              icon="timeline"
              search=""
              title="Timeline Game"
            />
          ) : null}
          <NavigationButton
            animateMenu={animateMenu}
            pathname="/endless-mode"
            icon="endless"
            search=""
            title="Endless"
          />
          {config.Credits !== undefined ? (
            <NavigationButton
              animateMenu={animateMenu}
              pathname="/credits"
              icon="credits"
              search=""
              title="Credits"
            />
          ) : null}
          {config.ShowStorybookCorner ? (
            <NavigationButton
              animateMenu={animateMenu}
              pathname="/story-book-corner"
              icon="storybook"
              search=""
              title="Story Book Corner"
            />
          ) : null}
          {config.ShowLinkButton ? (
            <NavigationButton
              external={true}
              pathname={`${config.Link}`}
              icon="preschool"
              title="Preschool Activity Center"
            />
          ) : null}
        </div>
      </nav>
    </div>
  );
};

export default NavigationMenu;
